import * as THREE from 'three';
import { GameState, LevelConfig, Difficulty } from '../types';
import { LEVEL } from '../constants';
import { Engine } from '../core/Engine';
import { GameLoop } from '../core/GameLoop';
import { EventEmitter } from '../core/EventEmitter';
import { InputManager } from '../input/InputManager';
import { Player } from './Player';
import { Weapon } from './Weapon';
import { Level } from './Level';
import { ShootingSystem } from './ShootingSystem';
import { MapBuilder } from '../maps/MapBuilder';
import { UIManager } from '../ui/UIManager';
import { SaveManager } from '../save/SaveManager';
import { WEAPONS } from '../data/weaponData';
import { ZOMBIES } from '../data/zombieData';
import { DIFFICULTY_MODIFIERS } from '../data/difficultyData';
import { setLanguage } from '../i18n/I18n';
import { Cutscene } from './Cutscene';
import { audioManager } from '../audio/AudioManager';
import { Tutorial } from './Tutorial';

export class Game {
  private engine: Engine;
  private loop: GameLoop;
  private input: InputManager;
  private events: EventEmitter;
  private ui: UIManager;
  private save: SaveManager;
  private mapBuilder: MapBuilder;

  private player!: Player;
  private level: Level | null = null;
  private shootingSystem!: ShootingSystem;

  private state: GameState = GameState.LOADING;
  private selectedWeapons = { slot1: 'pistol', slot2: 'pistol', slot3: '' };
  private selectedStage = 1;
  private selectedLevel = 1;
  private mapColliders: THREE.Box3[] = [];
  private coinBuffer = 0; // Coins earned during level

  // Tutorial
  private tutorial: Tutorial | null = null;
  private tutorialActive = false;
  private hasCompletedTutorial = false;

  // Armor pickup (stage level 1)
  private armorPickup: THREE.Group | null = null;
  private armorPickupLight: THREE.PointLight | null = null;

  // Laser sight
  private laserLine: THREE.Line | null = null;
  private laserDot: THREE.Mesh | null = null;
  private laserRaycaster = new THREE.Raycaster();

  // Level clear key & exit door
  private levelKey: THREE.Group | null = null;
  private levelKeyLight: THREE.PointLight | null = null;
  private levelClearOverlay: HTMLElement | null = null;
  private waitingForKeyPickup = false;
  private hasLevelKey = false;
  private exitDoor: THREE.Group | null = null;
  private exitDoorPos = new THREE.Vector3();
  private doorOpening = false;
  private doorOpenProgress = 0;
  private doorTransitioning = false;
  private lastFootstepTime = 0;
  private lastPlayerPos = new THREE.Vector3();
  private carLights: THREE.PointLight[] = [];
  private smokeMeshes: THREE.Mesh[] = [];

  constructor() {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    this.engine = new Engine(canvas);
    this.loop = new GameLoop();
    this.input = new InputManager();
    this.events = new EventEmitter();
    this.save = new SaveManager();
    this.ui = new UIManager(this.events, this.save);
    this.mapBuilder = new MapBuilder(this.engine.scene);
    this.shootingSystem = new ShootingSystem(this.engine.scene, this.events);

    // Set language from save
    setLanguage(this.save.getSettings().language);

    this.setupEvents();

    // Pointer lock on canvas click (only during gameplay)
    this.engine.canvas.addEventListener('click', () => {
      if (this.state === GameState.PLAYING && !this.input.isPointerLocked()) {
        this.input.requestPointerLock(this.engine.canvas);
      }
    });

    this.init();
  }

  private setupEvents(): void {
    // UI navigation
    this.events.on('ui:navigate', (state: GameState) => {
      this.transition(state);
    });

    this.events.on('ui:debugEscapeCutscene', async () => {
      this.ui.clearScreen();
      await this.playEscapeCutscene();
      this.transition(GameState.HOME);
    });

    this.events.on('ui:debugCutscene', async (data: { type: string; stage?: number }) => {
      this.ui.clearScreen();
      if (data.type === 'intro') {
        const cutscene = new Cutscene(this.engine.renderer);
        await cutscene.playIntro();
      } else if (data.type === 'escape') {
        await this.playEscapeCutscene();
      } else if (data.type === 'stage' && data.stage) {
        await this.playStageClearCutscene(data.stage);
      }
      this.transition(GameState.HOME);
    });

    this.events.on('ui:weaponSelected', (data: { slot1: string; slot2: string; slot3: string }) => {
      this.selectedWeapons = data;
    });

    this.events.on('ui:levelSelected', (data: { stage: number; level: number }) => {
      this.selectedStage = data.stage;
      this.selectedLevel = data.level;

      // Stage 1 Level 1, first time: play intro cutscene + tutorial
      if (data.stage === 1 && data.level === 1 && !this.hasCompletedTutorial) {
        this.startWithCutscene();
      } else {
        this.startGame();
      }
    });

    this.events.on('ui:resume', () => {
      this.transition(GameState.PLAYING);
    });

    this.events.on('ui:retry', () => {
      this.startGame();
    });

    this.events.on('ui:nextLevel', (data: { stage: number; level: number }) => {
      this.selectedStage = data.stage;
      this.selectedLevel = data.level;
    });

    // Tutorial gun pickup - give player the weapon during tutorial
    this.events.on('tutorial:gunPickedUp', () => {
      if (this.player) {
        const pistolConfig = WEAPONS['pistol'];
        const pistol = new Weapon(pistolConfig, 1, this.events);
        this.player.setWeapons(pistol, null);
        pistol.attachToCamera(this.engine.camera);
        this.createLaserSight();
      }
    });

    this.events.on('tutorial:armorPickedUp', () => {
      if (this.player) {
        this.player.addArmor(50); // Full armor
      }
    });

    // Game events
    this.events.on('zombie:killed', (data: { zombie: any; reward: number }) => {
      this.coinBuffer += data.reward;
      if (this.level) {
        this.level.onZombieKilled(data.zombie);
      }
      // Boss drops armor pickup
      if (data.zombie.isBoss) {
        const dropPos = data.zombie.position.clone();
        this.spawnArmorPickup(dropPos);
      }
    });

    this.events.on('level:clear', (data: { stage: number; level: number; stats: any; lastZombiePos?: THREE.Vector3 }) => {
      // Apply difficulty coin multiplier (data only, no save yet)
      const diffMods = DIFFICULTY_MODIFIERS[this.save.getSettings().difficulty as Difficulty];
      const totalCoins = Math.round(data.stats.coins * diffMods.coinMultiplier);
      data.stats.coins = totalCoins;
      this.coinBuffer = 0;

      // All levels (including level 10 boss): same flow — key → exit door → clear
      {
        (this as any)._pendingClearData = data;
        this.showLevelClearText();
        requestAnimationFrame(() => {
          this.save.addCoins(totalCoins);
          this.save.completeLevel(data.stage, data.level);
          if (data.lastZombiePos) {
            this.spawnLevelKey(data.lastZombiePos);
          }
          this.waitingForKeyPickup = true;
        });
      }
    });

    this.events.on('player:dead', () => {
      setTimeout(() => {
        this.transition(GameState.GAME_OVER);
      }, 1000);
    });

    this.events.on('player:damaged', () => {
      this.ui.showDamageOverlay();
    });
  }

  private init(): void {
    // Hide loading screen
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      const bar = document.getElementById('loading-bar');
      if (bar) bar.style.width = '100%';
      setTimeout(() => {
        loadingScreen.style.opacity = '0';
        setTimeout(() => loadingScreen.remove(), 500);
      }, 500);
    }

    this.transition(GameState.HOME);
  }

  private transition(newState: GameState, data?: any): void {
    const oldState = this.state;
    this.state = newState;

    // Exit old state
    if (oldState === GameState.PLAYING) {
      this.loop.stop();
      if (!this.input.isMobile) {
        try { document.exitPointerLock(); } catch (_) {}
      }
    }

    // Enter new state
    switch (newState) {
      case GameState.HOME:
        this.cleanupLevel();
        this.ui.showScreen(GameState.HOME);
        break;

      case GameState.WEAPON_SELECT:
        this.ui.showScreen(GameState.WEAPON_SELECT);
        break;

      case GameState.STAGE_SELECT:
        this.ui.showScreen(GameState.STAGE_SELECT, data);
        break;

      case GameState.PLAYING:
        if (oldState === GameState.PAUSED) {
          // Resume from pause
          this.ui.showScreen(GameState.PLAYING);
          this.loop.start();
          // Pointer lock: wait a bit then re-acquire on next click
          // Browser blocks immediate lock after exitPointerLock
          if (!this.input.isMobile) {
            setTimeout(() => {
              const resumeLock = () => {
                this.input.requestPointerLock(this.engine.canvas);
                this.engine.canvas.removeEventListener('click', resumeLock);
              };
              this.engine.canvas.addEventListener('click', resumeLock);
            }, 300);
          }
        }
        break;

      case GameState.PAUSED:
        this.loop.stop();
        if (!this.input.isMobile) try { document.exitPointerLock(); } catch (_) {}
        this.ui.showScreen(GameState.PAUSED);
        break;

      case GameState.LEVEL_CLEAR:
        this.loop.stop();
        this.ui.showScreen(GameState.LEVEL_CLEAR, data);
        break;

      case GameState.GAME_OVER:
        this.loop.stop();
        this.ui.showScreen(GameState.GAME_OVER);
        break;
    }
  }

  private async playEscapeCutscene(): Promise<void> {
    const cutscene = new Cutscene(this.engine.renderer);
    await cutscene.playEscape();
  }

  private async playStageClearCutscene(stage: number): Promise<void> {
    const cutscene = new Cutscene(this.engine.renderer);
    await cutscene.playStageClear(stage);
  }

  private async startWithCutscene(): Promise<void> {
    this.cleanupLevel();
    this.ui.clearScreen();

    // Play intro cutscene with 3D animation
    const cutscene = new Cutscene(this.engine.renderer);
    await cutscene.playIntro();

    // Start tutorial
    const difficulty = this.save.getSettings().difficulty as Difficulty;
    const diffMods = DIFFICULTY_MODIFIERS[difficulty];

    this.player = new Player(
      this.engine.camera,
      this.input,
      this.engine.scene,
      this.events,
      diffMods.playerHp,
      diffMods.regenRate
    );

    this.tutorial = new Tutorial(
      this.engine.scene,
      this.engine.camera,
      this.input,
      this.events
    );

    const tutorialPromise = this.tutorial.start();
    this.tutorialActive = true;

    // Tutorial BGM
    audioManager.init();
    audioManager.startTutorialBGM();
    this.player.controller.setColliders(this.tutorial.getColliders());

    // Show minimal HUD
    this.ui.showScreen(GameState.PLAYING);
    this.state = GameState.PLAYING;

    // Show mobile controls
    if (this.input.isMobile) this.input.showMobileControls();

    // Tutorial game loop
    this.loop.onUpdate((dt) => this.updateTutorial(dt));
    this.loop.start();

    // Request pointer lock
    setTimeout(() => {
      this.input.requestPointerLock(this.engine.canvas);
    }, 100);

    // Wait for tutorial completion
    await tutorialPromise;
    this.tutorialActive = false;
    this.hasCompletedTutorial = true;
    this.loop.stop();

    // Now start the actual game
    this.startGame();
  }

  private updateTutorial(dt: number): void {
    if (this.state !== GameState.PLAYING) return;

    if (this.input.isKeyJustPressed('Escape')) {
      this.transition(GameState.PAUSED);
      return;
    }

    // Player movement
    this.player.update(dt, this.engine.camera);

    // Footstep sounds in tutorial
    const tPos = this.player.controller.getPosition();
    const tMoved = tPos.distanceTo(this.lastPlayerPos);
    this.lastPlayerPos.copy(tPos);
    if (tMoved > 0.02) {
      const tNow = performance.now() / 1000;
      if (tNow - this.lastFootstepTime > 0.5) {
        audioManager.playFootstep();
        this.lastFootstepTime = tNow;
      }
    }

    // Flashlight toggle
    if (this.input.isKeyJustPressed('KeyF')) {
      this.player.flashlight.toggle();
    }

    // Tutorial update
    if (this.tutorial) {
      this.tutorial.update(dt);
    }

    // Shooting during tutorial (after gun pickup)
    if (this.player.activeWeapon && this.tutorial) {
      const weapon = this.player.activeWeapon;
      if (this.input.isMouseJustPressed(0) && weapon.fire()) {
        // audioManager.playGunshot();
      }
    }

    // Update HUD
    const weapon = this.player.activeWeapon;
    this.ui.updateHUD({
      hp: this.player.hp,
      maxHp: this.player.maxHp,
      weaponName: weapon ? weapon.config.name : '--',
      weaponLevel: weapon ? weapon.level : 0,
      currentAmmo: weapon ? weapon.currentAmmo : 0,
      maxAmmo: weapon ? weapon.getMaxAmmo() : 0,
      isReloading: weapon ? weapon.isReloading : false,
      reloadProgress: weapon ? weapon.getReloadProgress() : 1,
      coins: 0,
      zombiesRemaining: 0,
      flashlightOn: this.player.flashlight.isOn,
      energy: this.player.controller.energy,
      maxEnergy: this.player.controller.maxEnergy,
      energyDepleted: this.player.controller.energyDepleted,
      armor: this.player.armor,
      maxArmor: this.player.maxArmor,
      playerX: this.engine.camera.position.x,
      playerZ: this.engine.camera.position.z,
      playerRotY: (() => {
        const dir = new THREE.Vector3();
        this.engine.camera.getWorldDirection(dir);
        return Math.atan2(dir.x, dir.z);
      })(),
    });

    // Update laser
    this.updateLaserSight();

    this.input.endFrame();
    this.engine.render();
  }

  private spawnArmorPickup(playerSpawn: THREE.Vector3): void {
    const group = new THREE.Group();
    const M = (c: number, r = 0.85, m = 0.05) => new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: m });
    // Vest front/back
    const vestFront = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.38, 0.06), M(0x2a3a2a));
    vestFront.position.z = 0.06;
    group.add(vestFront);
    const vestBack = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.38, 0.06), M(0x2a3a2a));
    vestBack.position.z = -0.06;
    group.add(vestBack);
    // Sides
    for (const s of [-1, 1]) {
      const side = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.32, 0.14), M(0x2a3a2a));
      side.position.set(s * 0.16, -0.02, 0);
      group.add(side);
    }
    // Straps
    for (const s of [-1, 1]) {
      const strap = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.04, 0.22), M(0x111111, 0.7));
      strap.position.set(s * 0.1, 0.2, 0);
      group.add(strap);
    }
    // Metal plate
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.28, 0.02), M(0x555555, 0.4, 0.6));
    plate.position.set(0, 0, 0.1);
    group.add(plate);
    // Collar
    const collar = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.06, 0.16), M(0x2a3a2a));
    collar.position.set(0, 0.22, 0);
    group.add(collar);

    // Position: 3 units in front of player spawn
    group.position.set(playerSpawn.x + 2, 1.0, playerSpawn.z);
    this.engine.scene.add(group);
    this.armorPickup = group;

    // Glow light
    this.armorPickupLight = new THREE.PointLight(0x44ddff, 2, 5);
    this.armorPickupLight.position.set(playerSpawn.x + 2, 1.5, playerSpawn.z);
    this.engine.scene.add(this.armorPickupLight);
  }

  private startGame(): void {
    this.cleanupLevel();

    const difficulty = this.save.getSettings().difficulty as Difficulty;
    const diffMods = DIFFICULTY_MODIFIERS[difficulty];

    const mapResult = this.mapBuilder.buildStage(this.selectedStage);
    this.mapColliders = mapResult.colliders;

    this.player = new Player(
      this.engine.camera,
      this.input,
      this.engine.scene,
      this.events,
      diffMods.playerHp,
      diffMods.regenRate
    );
    this.player.controller.setColliders(this.mapColliders);

    const makeWeapon = (id: string): Weapon | null => {
      if (!id || !WEAPONS[id]) return null;
      const config = WEAPONS[id];
      const level = this.save.getWeaponLevel(id) || 1;
      return new Weapon(config, level, this.events);
    };
    const w1 = makeWeapon(this.selectedWeapons.slot1);
    const w2 = makeWeapon(this.selectedWeapons.slot2);
    const w3 = makeWeapon(this.selectedWeapons.slot3);

    this.player.setWeapons(w1, w2, w3);
    if (w1) w1.attachToCamera(this.engine.camera);

    // Stage 5 Level 1: unlock Assault Rifle
    if (this.selectedStage === 5 && this.selectedLevel === 1) {
      // Auto-own the assault rifle
      if (!this.save.isWeaponOwned('assaultRifle')) {
        this.save.purchaseWeapon('assaultRifle', 0);
      }
      // Give it in an empty slot
      const arConfig = WEAPONS['assaultRifle'];
      if (arConfig) {
        const ar = new Weapon(arConfig, this.save.getWeaponLevel('assaultRifle') || 1, this.events);
        const emptySlot = this.player.weapons.findIndex(w => w === null);
        if (emptySlot >= 0) {
          this.player.weapons[emptySlot] = ar;
        } else {
          // Replace slot 3 if all full
          if (this.player.weapons[2]) this.player.weapons[2].detachFromCamera(this.engine.camera);
          this.player.weapons[2] = ar;
        }
      }
    }

    // Every level starts with armor
    this.player.addArmor(50);

    // Determine available zombie types for this stage
    const availableTypes = Object.keys(ZOMBIES).filter(
      id => ZOMBIES[id].minStage <= this.selectedStage
    );

    // Create level config
    const levelIdx = this.selectedLevel - 1;
    const levelConfig: LevelConfig = {
      stage: this.selectedStage,
      level: this.selectedLevel,
      zombieCount: LEVEL.ZOMBIE_COUNTS[levelIdx] ?? 5,
      zombieTypes: availableTypes,
      hpMultiplier: (LEVEL.HP_MULTIPLIERS[levelIdx] ?? 1) * diffMods.zombieHpMultiplier,
      damageMultiplier: (LEVEL.DAMAGE_MULTIPLIERS[levelIdx] ?? 1) * diffMods.zombieDamageMultiplier,
      speedMultiplier: (LEVEL.SPEED_MULTIPLIERS[levelIdx] ?? 1) * diffMods.zombieSpeedMultiplier,
      hasBoss: this.selectedLevel === 10,
    };

    this.level = new Level(levelConfig, this.events);
    this.level.start(this.engine.scene, this.mapColliders);
    this.shootingSystem.cacheWallMeshes();

    // Reset coin buffer
    this.coinBuffer = 0;

    // Camera reset
    this.engine.camera.position.set(
      mapResult.playerSpawn.x,
      mapResult.playerSpawn.y,
      mapResult.playerSpawn.z
    );
    this.engine.camera.rotation.set(0, 0, 0);

    // Start game loop
    // Create laser sight
    this.createLaserSight();

    this.ui.showScreen(GameState.PLAYING);
    this.state = GameState.PLAYING;

    // Start gameplay BGM
    audioManager.init();
    audioManager.startGameplayBGM();

    // Cache animated objects for stage 2 (avoid scene.traverse every frame)
    this.carLights = [];
    this.smokeMeshes = [];
    if (this.selectedStage === 2) {
      this.engine.scene.traverse(obj => {
        if (obj instanceof THREE.PointLight && obj.userData.isCarLight) this.carLights.push(obj);
        if (obj instanceof THREE.Mesh && obj.userData.isSmoke) this.smokeMeshes.push(obj);
      });
    }

    // Clean up previous armor pickup
    this.armorPickup = null;
    if (this.armorPickupLight) {
      this.engine.scene.remove(this.armorPickupLight);
      this.armorPickupLight = null;
    }

    // Show mobile controls
    if (this.input.isMobile) this.input.showMobileControls();

    this.loop.onUpdate((dt) => this.update(dt));
    this.loop.start();

    // Initial pointer lock
    setTimeout(() => {
      if (this.state === GameState.PLAYING) {
        this.input.requestPointerLock(this.engine.canvas);
      }
    }, 100);

  }

  private laserBeam: THREE.Mesh | null = null;
  private laserGlow: THREE.PointLight | null = null;

  private createLaserSight(): void {
    // Skip laser sight on mobile for performance
    if (this.input.isMobile) return;
    // Remove old
    if (this.laserLine) { this.engine.scene.remove(this.laserLine); this.laserLine = null; }
    if (this.laserDot) { this.engine.scene.remove(this.laserDot); this.laserDot = null; }
    if (this.laserBeam) { this.engine.scene.remove(this.laserBeam); this.laserBeam = null; }
    if (this.laserGlow) { this.engine.scene.remove(this.laserGlow); this.laserGlow = null; }

    // Thick laser beam (cylinder)
    const beamGeo = new THREE.CylinderGeometry(0.005, 0.005, 1, 6);
    beamGeo.rotateX(Math.PI / 2);
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.7,
    });
    this.laserBeam = new THREE.Mesh(beamGeo, beamMat);
    this.engine.scene.add(this.laserBeam);

    // Laser dot (big, bright, glowing)
    const dotGeo = new THREE.SphereGeometry(0.04, 12, 8);
    const dotMat = new THREE.MeshBasicMaterial({
      color: 0xff2200,
    });
    this.laserDot = new THREE.Mesh(dotGeo, dotMat);
    this.engine.scene.add(this.laserDot);

    // Glow light at dot
    this.laserGlow = new THREE.PointLight(0xff0000, 2, 3);
    this.engine.scene.add(this.laserGlow);
  }

  private updateLaserSight(): void {
    if (!this.laserBeam || !this.laserDot || !this.laserGlow) return;

    const camera = this.engine.camera;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);

    const right = new THREE.Vector3();
    right.crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
    const laserStart = camera.position.clone()
      .add(right.clone().multiplyScalar(0.05))
      .add(new THREE.Vector3(0, -0.08, 0));

    // Raycast
    this.laserRaycaster.set(laserStart, direction);
    this.laserRaycaster.far = 50;
    const intersects = this.laserRaycaster.intersectObjects(this.engine.scene.children, true);

    let hitPoint: THREE.Vector3 | undefined;
    let hitDistance = 50;

    for (const hit of intersects) {
      if (hit.object === this.laserBeam || hit.object === this.laserDot) continue;
      if (hit.object instanceof THREE.Light) continue;
      hitPoint = hit.point;
      hitDistance = hit.distance;
      break;
    }

    if (!hitPoint) {
      hitPoint = laserStart.clone().add(direction.clone().multiplyScalar(50));
    }

    // Update beam (stretch cylinder from start to hit)
    const midPoint = laserStart.clone().lerp(hitPoint, 0.5);
    this.laserBeam.position.copy(midPoint);
    this.laserBeam.scale.set(1, 1, hitDistance);
    this.laserBeam.lookAt(hitPoint);

    // Pulsing beam thickness
    const pulse = 0.8 + Math.sin(performance.now() * 0.008) * 0.2;
    this.laserBeam.scale.x = pulse;
    this.laserBeam.scale.y = pulse;

    // Update dot
    this.laserDot.position.copy(hitPoint);
    const dotScale = Math.max(0.6, Math.min(1.5, hitDistance * 0.06));
    this.laserDot.scale.setScalar(dotScale);

    // Glow light at dot
    this.laserGlow.position.copy(hitPoint);
    this.laserGlow.intensity = 1.5 + Math.sin(performance.now() * 0.01) * 0.5;
  }

  private update(dt: number): void {
    if (this.state !== GameState.PLAYING) return;

    // Pause check
    if (this.input.isKeyJustPressed('Escape')) {
      this.transition(GameState.PAUSED);
      return;
    }

    // Player update
    this.player.update(dt, this.engine.camera);

    // Footstep sounds when moving
    const pPos = this.player.controller.getPosition();
    const moved = pPos.distanceTo(this.lastPlayerPos);
    this.lastPlayerPos.copy(pPos);
    if (moved > 0.02) {
      const now = performance.now() / 1000;
      const interval = moved > 0.1 ? 0.35 : 0.5; // faster when sprinting
      if (now - this.lastFootstepTime > interval) {
        audioManager.playFootstep();
        this.lastFootstepTime = now;
      }
    }

    // Flashlight toggle
    if (this.input.isKeyJustPressed('KeyF')) {
      this.player.flashlight.toggle();
    }

    // Weapon switch (1, 2, 3 keys or scroll)
    let switchTo = -1;
    if (this.input.isKeyJustPressed('Digit1')) switchTo = 0;
    if (this.input.isKeyJustPressed('Digit2')) switchTo = 1;
    if (this.input.isKeyJustPressed('Digit3')) switchTo = 2;
    if (switchTo >= 0 && switchTo !== this.player.activeSlot) {
      const currentWeapon = this.player.activeWeapon;
      if (currentWeapon) currentWeapon.detachFromCamera(this.engine.camera);
      this.player.switchToSlot(switchTo);
      if (this.player.activeWeapon) this.player.activeWeapon.attachToCamera(this.engine.camera);
    }

    // Reload
    if (this.input.isKeyJustPressed('KeyR') && this.player.activeWeapon) {
      this.player.activeWeapon.reload();
    }

    // Shooting
    if (this.player.activeWeapon && this.level) {
      const weapon = this.player.activeWeapon;
      const shouldFire = weapon.config.isAutomatic
        ? this.input.isFiring()
        : this.input.isMouseJustPressed(0);

      if (shouldFire && weapon.fire()) {
        this.shootingSystem.shoot(
          this.engine.camera,
          this.level.zombies,
          weapon
        );
        // audioManager.playGunshot();
      }
    }

    // Armor pickup interaction
    if (this.armorPickup) {
      this.armorPickup.rotation.y += dt * 1.5;
      this.armorPickup.position.y = 1.0 + Math.sin(performance.now() * 0.003) * 0.05;
      const pPos = this.player.controller.getPosition();
      const dx = pPos.x - this.armorPickup.position.x;
      const dz = pPos.z - this.armorPickup.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < 2.5 && (this.input.isKeyJustPressed('KeyE') || this.input.touchInteract)) {
        this.player.addArmor(50);
        this.engine.scene.remove(this.armorPickup);
        this.armorPickup = null;
        if (this.armorPickupLight) {
          this.engine.scene.remove(this.armorPickupLight);
          this.armorPickupLight = null;
        }
      }
    }

    // Bullet projectiles update
    this.shootingSystem.update(dt);

    // Level update
    if (this.level) {
      this.level.update(dt, this.player.controller.getPosition(), this.engine.camera);

      // Zombie attacks on player
      const attacks = this.level.getAttackingZombies(this.player.controller.getPosition());
      for (const attack of attacks) {
        this.player.takeDamage(attack.damage);
        this.ui.showDamageOverlay();
        // Screen shake
        this.engine.camera.rotation.z += (Math.random() - 0.5) * 0.03;
      }

      // Update spitter projectiles
      this.level.updateProjectiles(dt, this.player.controller.getPosition());

      // Boss ability / spitter projectile damage
      if (this.level.pendingPlayerDamage > 0) {
        this.player.takeDamage(this.level.pendingPlayerDamage);
        this.ui.showDamageOverlay();
        this.engine.camera.rotation.z += (Math.random() - 0.5) * 0.06;
        this.level.pendingPlayerDamage = 0;
      }
    }

    // Update HUD
    const weapon = this.player.activeWeapon;
    this.ui.updateHUD({
      hp: this.player.hp,
      maxHp: this.player.maxHp,
      weaponName: weapon ? weapon.config.name : '',
      weaponLevel: weapon ? weapon.level : 1,
      currentAmmo: weapon ? weapon.currentAmmo : 0,
      maxAmmo: weapon ? weapon.getMaxAmmo() : 0,
      isReloading: weapon ? weapon.isReloading : false,
      reloadProgress: weapon ? weapon.getReloadProgress() : 1,
      coins: this.save.getCoins() + this.coinBuffer,
      zombiesRemaining: this.level ? this.level.getAliveCount() : 0,
      flashlightOn: this.player.flashlight.isOn,
      energy: this.player.controller.energy,
      maxEnergy: this.player.controller.maxEnergy,
      energyDepleted: this.player.controller.energyDepleted,
      armor: this.player.armor,
      maxArmor: this.player.maxArmor,
      isADS: this.player.controller.isADS,
      playerX: this.engine.camera.position.x,
      playerZ: this.engine.camera.position.z,
      playerRotY: (() => {
        const dir = new THREE.Vector3();
        this.engine.camera.getWorldDirection(dir);
        return Math.atan2(dir.x, dir.z);
      })(),
      exitDoorX: this.hasLevelKey && this.exitDoor ? this.exitDoorPos.x : undefined,
      exitDoorZ: this.hasLevelKey && this.exitDoor ? this.exitDoorPos.z : undefined,
      stageId: this.selectedStage,
    });

    // Level key pickup phase
    if (this.waitingForKeyPickup && this.levelKey && !this.hasLevelKey) {
      // Animate key (float + rotate)
      this.levelKey.rotation.y += dt * 2.5;
      this.levelKey.position.y = this.levelKey.userData.baseY + Math.sin(performance.now() * 0.003) * 0.1;

      // Check distance to key
      const keyPos = this.levelKey.position;
      const pPos = this.player.controller.getPosition();
      const dx = keyPos.x - pPos.x;
      const dz = keyPos.z - pPos.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < 2.5) {
        this.ui.showInteractHint('[E] 鍵を拾う');
        if (this.input.isKeyJustPressed('KeyE')) {
          this.pickupLevelKey();
        }
      }
    }

    // Animate car lights and smoke (city stage, cached arrays)
    if (this.selectedStage === 2) {
      const t = performance.now() * 0.001;
      for (const light of this.carLights) {
        light.intensity = Math.random() > 0.7 ? 1.5 + Math.random() * 1.5 : 0;
      }
      for (const smoke of this.smokeMeshes) {
        smoke.position.y = smoke.userData.baseY + Math.sin(t * smoke.userData.speed + smoke.id) * 0.15 + (t * 0.05) % 1.0;
        const mat = smoke.material as THREE.MeshStandardMaterial;
        mat.opacity = 0.3 - ((t * 0.05) % 1.0) * 0.2;
        if (mat.opacity < 0.05) {
          smoke.position.y = smoke.userData.baseY;
          mat.opacity = 0.3;
        }
      }
    }

    // Animate exit warning light (city stage — single light)
    if (this.exitDoor && this.selectedStage === 2) {
      for (const child of this.exitDoor.children) {
        if (child instanceof THREE.PointLight && child.userData.isWarningLight) {
          child.intensity = Math.sin(performance.now() * 0.008) > 0 ? 3 : 0.2;
        }
      }
    }

    // Exit door interaction phase (after key is picked up)
    if (this.hasLevelKey && this.exitDoor && !this.doorOpening) {
      const pPos = this.player.controller.getPosition();
      const dx = this.exitDoorPos.x - pPos.x;
      const dz = this.exitDoorPos.z - pPos.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < 3.0) {
        const hint = this.selectedStage === 2 ? '[E] バリケードを解除する' : '[E] 鍵を使ってドアを開ける';
        this.ui.showInteractHint(hint);
        if (this.input.isKeyJustPressed('KeyE')) {
          this.doorOpening = true;
          this.doorOpenProgress = 0;
          audioManager.playDoorUnlock();
        }
      }
    }

    // Exit opening animation
    if (this.doorOpening && this.exitDoor) {
      this.doorOpenProgress += dt * 1.2;

      if (this.selectedStage === 2) {
        // City: barrier bar lifts up
        const bar = this.exitDoor.children[0] as THREE.Mesh;
        if (bar) {
          const liftP = Math.min(this.doorOpenProgress / 1.2, 1);
          bar.position.y = 1.0 + liftP * 1.8; // lifts from 1.0 to 2.8
          bar.rotation.z = liftP * 0.1; // slight tilt
        }
        // Stripes follow bar
        for (let i = 1; i <= 6; i++) {
          const stripe = this.exitDoor.children[i];
          if (stripe) {
            const liftP = Math.min(this.doorOpenProgress / 1.2, 1);
            stripe.position.y = 1.0 + liftP * 1.8;
          }
        }
      } else {
        // Lab: swing door open
        const doorMesh = this.exitDoor.children[0] as THREE.Mesh;
        if (doorMesh) {
          doorMesh.rotation.y = Math.min(this.doorOpenProgress * Math.PI / 2, Math.PI / 2);
          doorMesh.position.z = Math.sin(doorMesh.rotation.y) * 0.45;
          doorMesh.position.x = -(1 - Math.cos(doorMesh.rotation.y)) * 0.45;
        }
      }

      if (this.doorOpenProgress >= 1.5 && !this.doorTransitioning) {
        // Door fully open — transition to next level
        this.doorTransitioning = true;
        this.finishLevelTransition();
      }
    }

    // Input end frame
    this.input.endFrame();

    // Update laser sight
    this.updateLaserSight();

    // Render
    this.engine.render();
  }

  private showLevelClearText(): void {
    if (this.levelClearOverlay) return;
    this.levelClearOverlay = document.createElement('div');
    this.levelClearOverlay.style.cssText = `
      position:fixed;top:8%;left:50%;transform:translateX(-50%);
      color:#44ff44;font-size:2.2rem;font-weight:bold;text-align:center;
      text-shadow:0 0 20px rgba(0,255,0,0.8),0 0 40px rgba(0,255,0,0.4);
      z-index:30;pointer-events:none;letter-spacing:0.15em;
      font-family:sans-serif;
    `;
    this.levelClearOverlay.textContent = 'LEVEL CLEAR';
    document.body.appendChild(this.levelClearOverlay);
  }

  private spawnLevelKey(pos: THREE.Vector3): void {
    const keyMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, roughness: 0.3, metalness: 0.8,
      emissive: 0xffd700, emissiveIntensity: 0.8,
    });
    // Simple key shape (2 meshes only)
    this.levelKey = new THREE.Group();
    this.levelKey.add(new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.12, 0.2), keyMat));
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.08, 0.08), keyMat);
    handle.position.set(0, 0, 0.12);
    this.levelKey.add(handle);

    this.levelKey.position.set(pos.x, 1.2, pos.z);
    this.levelKey.userData.baseY = 1.2;
    this.engine.scene.add(this.levelKey);

    this.levelKeyLight = new THREE.PointLight(0xffd700, 3, 8);
    this.levelKeyLight.position.set(pos.x, 1.5, pos.z);
    this.engine.scene.add(this.levelKeyLight);
  }

  private pickupLevelKey(): void {
    audioManager.playKeyPickup();
    // Remove key from scene
    if (this.levelKey) {
      this.engine.scene.remove(this.levelKey);
      this.levelKey = null;
    }
    if (this.levelKeyLight) {
      this.engine.scene.remove(this.levelKeyLight);
      this.levelKeyLight = null;
    }

    this.hasLevelKey = true;

    // Show hint based on stage
    if (this.levelClearOverlay) {
      if (this.selectedStage === 2) {
        this.levelClearOverlay.textContent = '鍵を手に入れた！脱出ルートへ向かえ';
      } else {
        this.levelClearOverlay.textContent = '鍵を手に入れた！出口のドアへ向かえ';
      }
    }

    // Defer exit spawn across frames (avoid lag spike)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.spawnExitDoor();
      });
    });
  }

  private spawnExitDoor(): void {
    if (this.selectedStage === 2) {
      this.spawnCityExit();
    } else {
      this.spawnLabExit();
    }
  }

  private spawnLabExit(): void {
    const doorGroup = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x555560, roughness: 0.4, metalness: 0.6 });

    // Door panel
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.4, 1.2), mat);
    panel.position.y = 1.2;
    doorGroup.add(panel);

    // Frame (single box with hole illusion)
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.15, 2.5, 1.4), new THREE.MeshStandardMaterial({ color: 0x666670, roughness: 0.35, metalness: 0.7 }));
    frame.position.y = 1.25;
    frame.position.x = 0.02;
    doorGroup.add(frame);

    // EXIT sign + light
    const exitSign = new THREE.Mesh(
      new THREE.BoxGeometry(0.01, 0.1, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x00ff00, emissive: 0x00ff00, emissiveIntensity: 0.8 })
    );
    exitSign.position.set(-0.08, 2.3, 0);
    doorGroup.add(exitSign);

    const doorLight = new THREE.PointLight(0x44ff44, 2, 8);
    doorLight.position.set(-0.5, 2.5, 0);
    doorGroup.add(doorLight);

    this.exitDoorPos.set(14.5, 0, 0);
    doorGroup.position.copy(this.exitDoorPos);
    doorGroup.userData.isMapObject = true;
    this.exitDoor = doorGroup;
    this.engine.scene.add(doorGroup);
  }

  /** City exit: simplified barricade gate */
  private spawnCityExit(): void {
    const exitGroup = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0xdd8800, roughness: 0.5 });

    // Barrier bar
    const bar = new THREE.Mesh(new THREE.BoxGeometry(4, 0.15, 0.15), mat);
    bar.position.y = 1.0;
    exitGroup.add(bar);

    // 2 posts
    const postMat = new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.4, metalness: 0.7 });
    for (const side of [-1, 1]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.5, 0.16), postMat);
      post.position.set(side * 2.1, 0.75, 0);
      exitGroup.add(post);
    }

    // Sign
    const sign = new THREE.Mesh(
      new THREE.BoxGeometry(2, 0.5, 0.05),
      new THREE.MeshStandardMaterial({ color: 0x005500, emissive: 0x004400, emissiveIntensity: 0.5 })
    );
    sign.position.set(0, 2.0, 0);
    exitGroup.add(sign);

    // Warning light (1 only)
    const warnLight = new THREE.PointLight(0xff6600, 2, 8);
    warnLight.position.set(0, 2.5, 0);
    warnLight.userData.isWarningLight = true;
    exitGroup.add(warnLight);

    // Green exit light
    const exitLight = new THREE.PointLight(0x44ff44, 3, 10);
    exitLight.position.set(0, 1.5, 1);
    exitGroup.add(exitLight);

    this.exitDoorPos.set(0, 0, -22);
    exitGroup.position.copy(this.exitDoorPos);
    exitGroup.userData.isMapObject = true;
    this.exitDoor = exitGroup;
    this.engine.scene.add(exitGroup);
  }

  private finishLevelTransition(): void {
    // Clean up state
    if (this.levelClearOverlay) {
      this.levelClearOverlay.remove();
      this.levelClearOverlay = null;
    }
    this.hasLevelKey = false;
    this.waitingForKeyPickup = false;
    this.doorOpening = false;
    this.doorOpenProgress = 0;
    if (this.exitDoor) {
      this.engine.scene.remove(this.exitDoor);
      this.exitDoor = null;
    }

    // Stop game loop immediately
    this.loop.stop();

    const data = (this as any)._pendingClearData;
    (this as any)._pendingClearData = null;
    this.doorTransitioning = false;

    // Build fallback data if missing
    const clearData = data ?? {
      stage: this.selectedStage,
      level: this.selectedLevel,
      stats: { kills: 0, coins: 0, time: 0 },
    };

    // Level 10: cutscene then clear screen
    if (this.selectedLevel === 10) {
      if (!this.input.isMobile) document.exitPointerLock();
      this.ui.clearScreen();
      const stage = this.selectedStage;
      const p = stage === 1 ? this.playEscapeCutscene() : this.playStageClearCutscene(stage);
      p.then(() => {
        this.transition(GameState.LEVEL_CLEAR, clearData);
      }).catch(() => {
        this.transition(GameState.LEVEL_CLEAR, clearData);
      });
      return;
    }

    // Normal levels: go to clear screen immediately
    if (!this.input.isMobile) document.exitPointerLock();
    this.transition(GameState.LEVEL_CLEAR, clearData);
  }

  private cleanupLevel(): void {
    this.loop.stop();
    audioManager.stopBGM();
    this.input.hideMobileControls();
    if (this.level) {
      this.level.cleanup(this.engine.scene);
      this.level = null;
    }
    if (this.player?.activeWeapon) {
      this.player.activeWeapon.detachFromCamera(this.engine.camera);
    }

    // Remove level key & exit door
    if (this.levelKey) { this.engine.scene.remove(this.levelKey); this.levelKey = null; }
    if (this.levelKeyLight) { this.engine.scene.remove(this.levelKeyLight); this.levelKeyLight = null; }
    if (this.levelClearOverlay) { this.levelClearOverlay.remove(); this.levelClearOverlay = null; }
    if (this.exitDoor) { this.engine.scene.remove(this.exitDoor); this.exitDoor = null; }
    this.waitingForKeyPickup = false;
    this.hasLevelKey = false;
    this.doorOpening = false;
    this.doorOpenProgress = 0;
    (this as any)._pendingClearData = null;

    // Remove laser sight
    if (this.laserBeam) { this.engine.scene.remove(this.laserBeam); this.laserBeam = null; }
    if (this.laserDot) { this.engine.scene.remove(this.laserDot); this.laserDot = null; }
    if (this.laserGlow) { this.engine.scene.remove(this.laserGlow); this.laserGlow = null; }
    this.laserLine = null;

    // Remove map objects
    const toRemove: THREE.Object3D[] = [];
    this.engine.scene.traverse(child => {
      if (child.userData.isMapObject) toRemove.push(child);
    });
    toRemove.forEach(obj => {
      this.engine.scene.remove(obj);
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (obj.material instanceof THREE.Material) obj.material.dispose();
      }
      if (obj instanceof THREE.Light) {
        obj.dispose();
      }
    });

    this.mapColliders = [];

    // Clean up tutorial UI elements (they're on document.body, not the overlay)
    if (this.tutorial) {
      this.tutorial.cleanup();
      this.tutorial = null;
      this.tutorialActive = false;
    }
  }
}
